import { useEffect, useRef, useState } from "react";
import { ModalShell } from "../../ModalShell";
import { EyeIcon, EyeOffIcon } from "../icons/EyeIcons";
import {
  SITE_LOGIN_MAX_NAME_LENGTH,
  type SaveSiteLoginInput,
  type SiteLogin,
} from "../../../hooks/useSiteLogins";
import dialogStyles from "../../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./SiteLoginModals.module.css";

type SaveSiteLoginModalProps = {
  // Non-null when rotating an existing login: the name is then fixed (it is
  // the identity other settings point at) and the password starts empty,
  // because it can't be read back to prefill.
  existing: SiteLogin | null;
  onClose: () => void;
  onSave: (input: SaveSiteLoginInput) => Promise<{ ok: boolean; message: string }>;
};

const trimmedOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const SaveSiteLoginModal = ({
  existing,
  onClose,
  onSave,
}: SaveSiteLoginModalProps): JSX.Element => {
  const [name, setName] = useState<string>(existing?.name ?? "");
  const [site, setSite] = useState<string>(existing?.site ?? "");
  const [username, setUsername] = useState<string>(existing?.username ?? "");
  const [password, setPassword] = useState<string>("");
  const [loginUrl, setLoginUrl] = useState<string>(existing?.loginUrl ?? "");
  const [usernameField, setUsernameField] = useState<string>(existing?.usernameField ?? "");
  const [passwordField, setPasswordField] = useState<string>(existing?.passwordField ?? "");
  const [show, setShow] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const firstRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => firstRef.current?.focus(), []);

  const canSubmit =
    name.trim().length > 0 &&
    site.trim().length > 0 &&
    username.trim().length > 0 &&
    password.length > 0 &&
    !submitting;
  const titleId = "save-site-login-title";
  const hasOverrides =
    (existing?.loginUrl ?? "") !== "" ||
    (existing?.usernameField ?? "") !== "" ||
    (existing?.passwordField ?? "") !== "";

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId} size="sm" width="min(520px, 92vw)">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>
          {existing ? `Update ${existing.name}` : "Add a website sign-in"}
        </h3>
        <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <form
        className={styles.form}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!canSubmit) return;
          setSubmitting(true);
          setError(null);
          const result = await onSave({
            name: name.trim(),
            site: site.trim(),
            username: username.trim(),
            password,
            loginUrl: trimmedOrUndefined(loginUrl),
            usernameField: trimmedOrUndefined(usernameField),
            passwordField: trimmedOrUndefined(passwordField),
          });
          setSubmitting(false);
          if (!result.ok) setError(result.message);
        }}
      >
        <label className={styles.field}>
          <span className={styles.label}>Name</span>
          <input
            ref={existing ? undefined : firstRef}
            type="text"
            className={styles.input}
            value={name}
            maxLength={SITE_LOGIN_MAX_NAME_LENGTH}
            placeholder="e.g. Ocean Facilities portal"
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            // Renaming would orphan whatever already points at this login,
            // so an existing one is updated in place instead.
            disabled={submitting || existing !== null}
            required
          />
          <span className={styles.hint}>
            {existing
              ? "Renaming isn't possible - add a new sign-in and remove this one."
              : "A name you'll recognise when picking it later."}
          </span>
        </label>

        {/* Load-bearing, not decoration - hence its own field rather than
            something inferred from the name. This sign-in is used against this
            website and nowhere else, which is what stops a page fetch being
            talked into signing in somewhere the customer never named. */}
        <label className={styles.field}>
          <span className={styles.label}>Website</span>
          <input
            type="text"
            className={styles.input}
            value={site}
            onChange={(e) => {
              setSite(e.target.value);
              if (error) setError(null);
            }}
            placeholder="portal.example.com"
            autoComplete="off"
            spellCheck={false}
            disabled={submitting}
            required
          />
          <span className={styles.hint}>
            The site this login is for. It&apos;s only ever used here.
          </span>
        </label>

        <div className={styles.pairRow}>
          <label className={styles.field}>
            <span className={styles.label}>Username</span>
            <input
              type="text"
              className={styles.input}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (error) setError(null);
              }}
              placeholder="you@example.com"
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore
              disabled={submitting}
              required
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Password</span>
            <div className={styles.inputWrap}>
              <input
                ref={existing ? firstRef : undefined}
                type={show ? "text" : "password"}
                className={styles.input}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={existing ? "Enter the new password" : ""}
                autoComplete="off"
                spellCheck={false}
                data-1p-ignore
                disabled={submitting}
                required
              />
              <button
                type="button"
                className={styles.revealBtn}
                onClick={() => setShow((prev) => !prev)}
                aria-label={show ? "Hide password" : "Show password"}
                aria-pressed={show}
                disabled={submitting}
                tabIndex={-1}
              >
                {show ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </label>
        </div>

        <details className={styles.advanced} open={hasOverrides}>
          <summary className={styles.advancedSummary}>The sign-in page is unusual</summary>
          <div className={styles.advancedBody}>
            <p className={styles.hint}>
              Normally we find the sign-in form by ourselves. Fill these in only if that
              doesn&apos;t work - you can copy the field names from the page&apos;s source.
            </p>
            <label className={styles.field}>
              <span className={styles.label}>Sign-in page address</span>
              <input
                type="url"
                className={styles.input}
                value={loginUrl}
                onChange={(e) => setLoginUrl(e.target.value)}
                placeholder="https://portal.example.com/login"
                autoComplete="off"
                spellCheck={false}
                disabled={submitting}
              />
            </label>
            <div className={styles.pairRow}>
              <label className={styles.field}>
                <span className={styles.label}>Username field name</span>
                <input
                  type="text"
                  className={styles.input}
                  value={usernameField}
                  onChange={(e) => setUsernameField(e.target.value)}
                  placeholder="email"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={submitting}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Password field name</span>
                <input
                  type="text"
                  className={styles.input}
                  value={passwordField}
                  onChange={(e) => setPasswordField(e.target.value)}
                  placeholder="password"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={submitting}
                />
              </label>
            </div>
          </div>
        </details>

        {error ? <p className={styles.errorBanner}>{error}</p> : null}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={!canSubmit}>
            {submitting ? "Saving…" : existing ? "Update sign-in" : "Save sign-in"}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};
